import { useState, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ClipboardList, Plus, ExternalLink, Award } from 'lucide-react';
import { toast } from 'sonner';

export default function TestsPage() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myScores, setMyScores] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [scoreDialog, setScoreDialog] = useState(false);
  const [form, setForm] = useState({ title: '', google_form_link: '', google_sheet_link: '', date: new Date().toISOString().slice(0, 10) });
  const [scoreForm, setScoreForm] = useState({ employee_id: '', score: '', max_score: '100' });

  const isManager = ['super_admin', 'management'].includes(user?.system_role);
  const canAddScores = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);

  const fetchData = async () => {
    try {
      const [testRes, empRes] = await Promise.all([
        api.get('/tests'),
        canAddScores ? api.get('/employees') : Promise.resolve({ data: [] })
      ]);
      setTests(testRes.data);
      setEmployees(empRes.data);
      if (user?.system_role === 'employee') {
        const myRes = await api.get(`/tests/employee/${user.id}`);
        setMyScores(myRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const loadLeaderboard = async (testId) => {
    try {
      const res = await api.get(`/tests/leaderboard/${testId}`);
      setLeaderboard(res.data);
      setSelectedTest(testId);
    } catch (err) { console.error(err); }
  };

  const handleCreateTest = async () => {
    try {
      await api.post('/tests', form);
      toast.success('Test created');
      setCreateDialog(false);
      setForm({ title: '', google_form_link: '', google_sheet_link: '', date: new Date().toISOString().slice(0, 10) });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleAddScore = async () => {
    try {
      await api.post(`/tests/${selectedTest}/scores`, {
        employee_id: scoreForm.employee_id,
        score: parseFloat(scoreForm.score),
        max_score: parseFloat(scoreForm.max_score)
      });
      toast.success('Score added');
      setScoreDialog(false);
      setScoreForm({ employee_id: '', score: '', max_score: '100' });
      loadLeaderboard(selectedTest);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tests-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Knowledge Tests</h1>
          <p className="text-neutral-500 text-sm mt-1">Staff knowledge assessment portal</p>
        </div>
        {isManager && (
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="create-test-button"
            onClick={() => setCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" /> Create Test
          </Button>
        )}
      </div>

      <Tabs defaultValue="tests" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="tests" className="rounded-sm" data-testid="tests-tab">Tests</TabsTrigger>
          {selectedTest && <TabsTrigger value="leaderboard" className="rounded-sm" data-testid="leaderboard-tab">Leaderboard</TabsTrigger>}
          {user?.system_role === 'employee' && <TabsTrigger value="myscores" className="rounded-sm" data-testid="my-scores-tab">My Scores</TabsTrigger>}
        </TabsList>

        <TabsContent value="tests" className="mt-4">
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-neutral-200 rounded animate-pulse" />)}</div>
          ) : tests.length === 0 ? (
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardContent className="py-12 text-center text-neutral-500">
                <ClipboardList className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No tests created yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tests.map(test => (
                <Card key={test.id} className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md transition-shadow duration-200"
                  data-testid={`test-card-${test.id}`}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-semibold text-neutral-900">{test.title}</h3>
                        <p className="text-xs text-neutral-500 mt-1">Date: {test.date}</p>
                        {test.google_form_link && (
                          <a href={test.google_form_link} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-red-600 hover:underline flex items-center gap-1 mt-1">
                            <ExternalLink className="h-3 w-3" /> Take Test
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => loadLeaderboard(test.id)}
                          data-testid={`view-leaderboard-${test.id}`}>
                          <Award className="h-3.5 w-3.5 mr-1.5" /> Leaderboard
                        </Button>
                        {canAddScores && (
                          <Button variant="outline" size="sm" className="rounded-sm"
                            onClick={() => { setSelectedTest(test.id); setScoreDialog(true); }}
                            data-testid={`add-score-${test.id}`}>
                            <Plus className="h-3.5 w-3.5 mr-1.5" /> Score
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-neutral-700">Leaderboard</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 w-12">#</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Score</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-neutral-500">No scores yet</TableCell></TableRow>
                  ) : leaderboard.map((s, i) => (
                    <TableRow key={s.id} className="hover:bg-neutral-50">
                      <TableCell className="font-bold text-sm">
                        {i < 3 ? (
                          <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold
                            ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-neutral-200 text-neutral-700' : 'bg-orange-100 text-orange-700'}`}>
                            {i + 1}
                          </span>
                        ) : i + 1}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-neutral-800">{s.employee_name}</TableCell>
                      <TableCell className="text-sm text-neutral-800">{s.score}/{s.max_score}</TableCell>
                      <TableCell className="text-sm text-neutral-600 hidden sm:table-cell">{Math.round((s.score / s.max_score) * 100)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="myscores" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-neutral-700">My Test History</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {myScores.length === 0 ? (
                <p className="text-sm text-neutral-500 py-8 text-center">No test scores recorded</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-neutral-50">
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Test</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Score</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myScores.map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="text-sm font-medium text-neutral-800">{s.test_title}</TableCell>
                        <TableCell className="text-sm text-neutral-800">{s.score}/{s.max_score}</TableCell>
                        <TableCell className="text-sm text-neutral-600">{Math.round((s.score / s.max_score) * 100)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Test Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md" data-testid="create-test-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Create Knowledge Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Test Title</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="rounded-sm" data-testid="test-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Google Form Link</Label>
              <Input value={form.google_form_link} onChange={e => setForm(p => ({ ...p, google_form_link: e.target.value }))}
                placeholder="https://forms.google.com/..." className="rounded-sm" data-testid="test-form-link-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Google Sheet Link (responses)</Label>
              <Input value={form.google_sheet_link} onChange={e => setForm(p => ({ ...p, google_sheet_link: e.target.value }))}
                placeholder="https://docs.google.com/spreadsheets/..." className="rounded-sm" data-testid="test-sheet-link-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Test Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                className="rounded-sm" data-testid="test-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreateTest} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="test-create-submit-button">
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Score Dialog */}
      <Dialog open={scoreDialog} onOpenChange={setScoreDialog}>
        <DialogContent className="max-w-sm" data-testid="add-score-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Add Test Score</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Employee</Label>
              <Select value={scoreForm.employee_id} onValueChange={v => setScoreForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="score-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Score</Label>
                <Input type="number" value={scoreForm.score} onChange={e => setScoreForm(p => ({ ...p, score: e.target.value }))}
                  className="rounded-sm" data-testid="score-value-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Max Score</Label>
                <Input type="number" value={scoreForm.max_score} onChange={e => setScoreForm(p => ({ ...p, max_score: e.target.value }))}
                  className="rounded-sm" data-testid="score-max-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScoreDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleAddScore} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="score-submit-button">
              Add Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
